import {
  CustomEventApplyToUrlsDto,
  CustomEventCollectionPageViewSettingsDto,
  CustomEventDto,
  CustomEventProductPageViewSettingsDto,
  CustomEventType,
} from "@intelligems/ig-types";
import * as rtk from "@reduxjs/toolkit";
import { createSelector, type PayloadAction } from "@reduxjs/toolkit";
import { fromGqlId } from "@src/api/formatters/shopify";
import { createNewEvent } from "@src/features/customEvents/helpers";
import { ICustomEventsState } from "@src/store/slices/customEvents/types";
import { ProductPickerSelectedProduct } from "@src/store/slices/productPicker/types";

const sliceInitialCustomEventsState: ICustomEventsState = {
  isSuccess: false,
  customEvents: [], // all custom events
  storedCustomEvents: [],
  currentEvent: undefined, // current
  archivingEventId: "",
  unarchivingEventId: "",
  selectedProducts: [],
  selectedProductsLoading: false,
};

const customEventsSlice = rtk.createSlice({
  name: "customEvents",
  initialState: sliceInitialCustomEventsState,
  reducers: {
    resetCustomEvents: () => {
      return sliceInitialCustomEventsState;
    },
    resetCustomEventsToStored: (draft) => {
      draft.customEvents = [];
    },
    initCustomEvents: (
      draft,
      action: PayloadAction<{ customEvents: CustomEventDto[] | undefined }>
    ) => {
      draft.storedCustomEvents = action.payload.customEvents ?? [];
      draft.customEvents = action.payload.customEvents ?? [];
      draft.isSuccess = true;
    },
    initNewCustomEvent: (
      draft,
      action: PayloadAction<{ eventType: CustomEventType }>
    ) => {
      draft.currentEvent = createNewEvent(
        action.payload.eventType === "javascriptEvent"
          ? "javascriptImportedEvent"
          : action.payload.eventType
      );
    },
    initCustomEvent: (
      draft,
      action: PayloadAction<{ customEvent: CustomEventDto }>
    ) => {
      draft.currentEvent = action.payload.customEvent;
    },
    resetNewCustomEvent: (draft) => {
      draft.currentEvent = undefined;
    },
    setcurrentEventName: (draft, action: PayloadAction<string>) => {
      if (draft.currentEvent) {
        draft.currentEvent.name = action.payload;
      }
    },
    setcurrentEventDescription: (draft, action: PayloadAction<string>) => {
      if (draft.currentEvent) {
        draft.currentEvent.description = action.payload;
      }
    },
    setCustomEventProducts: (
      draft,
      action: PayloadAction<{
        products: CustomEventProductPageViewSettingsDto;
      }>
    ) => {
      if (draft.currentEvent?.type === "productPageView") {
        draft.currentEvent.settings = action.payload.products;
      }
    },
    removeCustomEventProduct: (
      draft,
      action: PayloadAction<{
        productId: string;
      }>
    ) => {
      const { productId } = action.payload;

      if (draft.currentEvent?.type === "productPageView") {
        draft.currentEvent.settings = draft.currentEvent.settings.filter(
          (product) => product.productId !== productId
        );
        draft.selectedProducts = draft.selectedProducts.filter(
          (p) => fromGqlId(p.id) !== productId
        );
      }
    },
    setCustomEventCollections: (
      draft,
      action: PayloadAction<{
        collections: CustomEventCollectionPageViewSettingsDto;
      }>
    ) => {
      if (draft.currentEvent?.type === "collectionPageView") {
        draft.currentEvent.settings = action.payload.collections;
      }
    },
    setCustomEventUrls: (
      draft,
      action: PayloadAction<{
        urls: CustomEventApplyToUrlsDto[];
      }>
    ) => {
      if (
        draft.currentEvent?.type === "pageView" ||
        draft.currentEvent?.type === "clickEvent" ||
        draft.currentEvent?.type === "elementViewed" ||
        draft.currentEvent?.type === "javascriptEvent" ||
        draft.currentEvent?.type === "scrollDepth"
      ) {
        draft.currentEvent.applyToUrls = action.payload.urls;
        draft.currentEvent.error = action.payload.urls
          .filter((url) => Boolean(url.error))
          .map((url) => url.error)
          .join(", ");
      }
    },
    setCustomEventSelectors: (
      draft,
      action: PayloadAction<{
        selectors: string[];
      }>
    ) => {
      if (
        draft.currentEvent?.type === "clickEvent" ||
        draft.currentEvent?.type === "elementViewed"
      ) {
        draft.currentEvent.settings.selectors = action.payload.selectors;
      }
    },
    setCustomEventScrollDepth: (
      draft,
      action: PayloadAction<{
        scrollDepth: number;
      }>
    ) => {
      if (draft.currentEvent?.type === "scrollDepth") {
        draft.currentEvent.settings = {
          mobile: { percentage: action.payload.scrollDepth },
          desktop: { percentage: action.payload.scrollDepth },
        };
      }
    },
    setCustomJsEventCode: (
      draft,
      action: PayloadAction<{
        code: string;
      }>
    ) => {
      if (draft.currentEvent?.type === "javascriptEvent") {
        draft.currentEvent.settings.code = action.payload.code;
      }
    },
    setCustomJsEventIdentifier: (
      draft,
      action: PayloadAction<{
        identifier: string;
      }>
    ) => {
      if (draft.currentEvent?.type === "javascriptImportedEvent") {
        draft.currentEvent.identifier = action.payload.identifier;
        draft.currentEvent.settings.registered = true;
      } else if (draft.currentEvent?.type === "javascriptEvent") {
        draft.currentEvent.identifier = action.payload.identifier;
      }
    },
    switchCustomJsEventType: (
      draft,
      action: PayloadAction<{ tab: number }>
    ) => {
      if (action.payload.tab === 0) {
        // Switch to javascriptEvent
        if (draft.currentEvent?.type === "javascriptImportedEvent") {
          draft.currentEvent = {
            ...draft.currentEvent,
            type: "javascriptEvent",
            settings: {
              code: "",
            },
            identifier: "", // Remove imported event-specific data
          };
        }
      } else if (action.payload.tab === 1) {
        // Switch to javascriptImportedEvent
        if (draft.currentEvent?.type === "javascriptEvent") {
          draft.currentEvent = {
            ...draft.currentEvent,
            type: "javascriptImportedEvent",
            settings: {
              registered: false,
            },
            applyToUrls: null,
          };
        }
      }
    },
    setArchivingEventId: (
      draft,
      action: PayloadAction<{
        id: string;
      }>
    ) => {
      draft.archivingEventId = action.payload.id;
    },
    setUnarchivingEventId: (
      draft,
      action: PayloadAction<{
        id: string;
      }>
    ) => {
      draft.unarchivingEventId = action.payload.id;
    },
    setCustomEventSelectedProducts: (
      draft,
      action: PayloadAction<ProductPickerSelectedProduct[]>
    ) => {
      draft.selectedProducts = action.payload;
    },
    setCustomEventSelectedProductsLoading: (
      draft,
      action: PayloadAction<boolean>
    ) => {
      draft.selectedProductsLoading = action.payload;
    },
    // setNewExpKeyMetricCustomEvent: (draft) => {
    //   if (draft.currentEvent) {
    //     draft.newExpKeyMetricCustomEvents.push({
    //       ...draft.currentEvent,
    //       isSelected: true,
    //     });
    //     draft.currentEvent = undefined;
    //   }
    //   return draft;
    // },
  },
  selectors: {
    selectedCustomEvents: (state) => state.customEvents,
    selectedCurrentCustomEvent: (state) => state.currentEvent,
    selectedArchivingEventId: (state) => state.archivingEventId,
    selectedUnarchivingEventId: (state) => state.unarchivingEventId,
    getCustomerEventSelectedProducts: (state) => state.selectedProducts,
    getCustomerEventSelectedProductsLoading: (state) =>
      state.selectedProductsLoading,
  },
});

export const {
  resetCustomEvents,
  resetCustomEventsToStored,
  initCustomEvents,
  initNewCustomEvent,
  resetNewCustomEvent,
  setcurrentEventDescription,
  setcurrentEventName,
  setCustomEventProducts,
  removeCustomEventProduct,
  setCustomEventCollections,
  setCustomEventUrls,
  setCustomEventSelectors,
  setCustomEventScrollDepth,
  setCustomJsEventIdentifier,
  setCustomJsEventCode,
  switchCustomJsEventType,
  initCustomEvent,
  setArchivingEventId,
  setUnarchivingEventId,
  setCustomEventSelectedProducts,
  setCustomEventSelectedProductsLoading,
} = customEventsSlice.actions;

export const customEventsReducer = customEventsSlice.reducer;
export const {
  selectedCustomEvents,
  selectedCurrentCustomEvent,
  selectedArchivingEventId,
  selectedUnarchivingEventId,
  getCustomerEventSelectedProducts,
  getCustomerEventSelectedProductsLoading,
} = customEventsSlice.selectors;

export const selectedSettingsByEventType = (eventType: CustomEventType) =>
  createSelector(
    (state: ICustomEventsState) => state.currentEvent,
    (currentEvent) => {
      if (currentEvent?.type === eventType) {
        return currentEvent.settings;
      }
      return null;
    }
  );
